import {Autocomplete} from '@mantine/core';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import type {ISelectOption} from 'src/types/Form';
import type {IFormFieldV2} from 'src/types/FormV2';

export interface FormAutocompleteV2Props {
  field: IFormFieldV2;
  value: string | number | null;
  error?: string;
  onChange: (
    name: string,
    value: string | number | null,
    displayValue?: string
  ) => void;
}

export const FormAutocompleteV2 = React.memo<FormAutocompleteV2Props>(
  ({field, value, error, onChange}: FormAutocompleteV2Props) => {
    const options = useMemo(() => field.options || [], [field.options]);

    const valueObject = useMemo<ISelectOption | null>(() => {
      return (
        options.find(item => item && String(item.id) === String(value)) || null
      );
    }, [value, options]);

    const data = useMemo(
      () => options.map(opt => ({value: String(opt.id), label: opt.label})),
      [options]
    );

    const [search, setSearch] = useState(valueObject?.label ?? '');

    useEffect(() => {
      setSearch(valueObject?.label ?? '');
    }, [valueObject]);

    const handleSearchChange = useCallback(
      (val: string) => {
        setSearch(val);
        if (val === '') {
          onChange(field.name, null, undefined);
        }
      },
      [field.name, onChange]
    );

    const handleOptionSubmit = useCallback(
      (val: string) => {
        const selectedOption = options.find(opt => String(opt.id) === val);
        if (!selectedOption) return;
        setSearch(selectedOption.label);
        onChange(
          field.name,
          selectedOption.id,
          selectedOption.text ?? selectedOption.label
        );
      },
      [field.name, onChange, options]
    );

    const handleBlur = useCallback(() => {
      setSearch(valueObject?.label ?? '');
    }, [valueObject]);

    return (
      <Autocomplete
        data-testid={`form-field-${field.name}`}
        label={field.label}
        description={field.description}
        error={error}
        withAsterisk={field.required}
        placeholder={
          field.placeholder ?? `Search ${field.label.toLowerCase()}`
        }
        disabled={field.disabled}
        value={search}
        onChange={handleSearchChange}
        onOptionSubmit={handleOptionSubmit}
        onBlur={handleBlur}
        data={data}
      />
    );
  }
);
